<template>
  <el-dialog
    v-model="dialogVisible"
    width="480px"
    align-center
    :show-close="false"
    class="profile-dialog"
    modal-class="profile-dialog-mask"
    header-class="profile-dialog__header"
    body-class="profile-dialog__body"
  >
    <div class="dialog-header">
      <h2 class="dialog-title">个人信息</h2>
      <el-icon class="close-icon" @click="dialogVisible = false"><Close /></el-icon>
    </div>

    <div class="dialog-body">
      <!-- 用户信息展示 -->
      <div class="user-info-section">
        <div class="avatar-wrapper">
          <img v-if="userInfoData.avatar" :src="userInfoData.avatar" class="avatar-img" />
          <img v-else src="@/assets/logo.png" class="avatar-img" />
          <div class="avatar-overlay" @click.stop="handleAvatarChange">
            <el-icon><Edit /></el-icon>
          </div>
        </div>

        <div class="nickname-row">
          <template v-if="isEditingNickname">
            <el-input
              ref="nicknameInputRef"
              v-model="nicknameValue"
              size="small"
              class="nickname-input"
              maxlength="20"
              @keyup.enter="confirmNickname"
              @keyup.esc="cancelNickname"
            ></el-input>
            <el-icon class="action-icon confirm-icon" @click="confirmNickname"><Check /></el-icon>
            <el-icon class="action-icon cancel-icon" @click="cancelNickname"><Close /></el-icon>
          </template>
          <template v-else>
            <span class="nickname-text">{{ userInfoData.nickname || "未设置昵称" }}</span>
            <el-icon class="action-icon edit-icon" @click="startEditNickname"><Edit /></el-icon>
          </template>
        </div>
      </div>

      <!-- 修改密码区域 -->
      <div class="password-section">
        <div class="section-title">修改密码</div>
        <el-form ref="passwordFormRef" :model="passwordForm" :rules="passwordRules" label-width="0">
          <el-form-item prop="oldPassword">
            <el-input
              v-model="passwordForm.oldPassword"
              type="password"
              placeholder="请输入原密码"
              show-password
            >
              <template #prefix>
                <el-icon><Lock /></el-icon>
              </template>
            </el-input>
          </el-form-item>
          <el-form-item prop="newPassword">
            <el-input
              v-model="passwordForm.newPassword"
              type="password"
              placeholder="请输入新密码(至少6位)"
              show-password
            >
              <template #prefix>
                <el-icon><Key /></el-icon>
              </template>
            </el-input>
          </el-form-item>
          <el-form-item prop="confirmPassword">
            <el-input
              v-model="passwordForm.confirmPassword"
              type="password"
              placeholder="请再次输入新密码"
              show-password
            >
              <template #prefix>
                <el-icon><Key /></el-icon>
              </template>
            </el-input>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" class="submit-btn" :loading="submitting" @click="handleChangePassword">
              确认修改
            </el-button>
          </el-form-item>
        </el-form>
      </div>
    </div>
  </el-dialog>
</template>

<script setup>
import { ref, reactive, nextTick, onMounted, onBeforeUnmount } from "vue";
import { ElMessage } from "element-plus";
import { Close, Edit, Check, Lock, Key } from "@element-plus/icons-vue";
import { EventBus, EVENTS } from "@/utils/eventBus";
import { userInfo, useredit } from "@/api";
import { uploadImage } from "@/utils/uploadImage";

defineOptions({
  name: "ProfileDialog",
});

const dialogVisible = ref(false);
const submitting = ref(false);
const userInfoData = ref({});
const isEditingNickname = ref(false);
const nicknameValue = ref("");
const nicknameInputRef = ref(null);
const passwordFormRef = ref(null);

const passwordForm = reactive({
  oldPassword: "",
  newPassword: "",
  confirmPassword: "",
});

const validateConfirm = (rule, value, callback) => {
  if (value !== passwordForm.newPassword) {
    callback(new Error("两次输入的密码不一致"));
  } else {
    callback();
  }
};

const passwordRules = {
  oldPassword: [{ required: true, message: "请输入原密码", trigger: "blur" }],
  newPassword: [{ required: true, message: "请输入新密码", trigger: "blur" }],
  confirmPassword: [
    { required: true, message: "请再次输入新密码", trigger: "blur" },
    { validator: validateConfirm, trigger: "blur" },
  ],
};

function showDialog() {
  dialogVisible.value = true;
  isEditingNickname.value = false;
  resetForm();
  getUserInfo();
}

function resetForm() {
  passwordForm.oldPassword = "";
  passwordForm.newPassword = "";
  passwordForm.confirmPassword = "";
  nextTick(() => {
    passwordFormRef.value && passwordFormRef.value.clearValidate();
  });
}

function getUserInfo() {
  const userId = localStorage.getItem("userId");
  if (!userId) return;
  userInfo({ userId }).then((res) => {
    if (res.code == 200) {
      userInfoData.value = res.data;
    }
  });
}

async function handleAvatarChange() {
  const imagePath = await uploadImage({});
  updateUserInfo({ avatar: imagePath });
}

function startEditNickname() {
  nicknameValue.value = userInfoData.value.nickname || "";
  isEditingNickname.value = true;
  nextTick(() => {
    nicknameInputRef.value && nicknameInputRef.value.focus();
  });
}

function confirmNickname() {
  const name = nicknameValue.value.trim();
  if (!name) {
    ElMessage.warning("昵称不能为空");
    return;
  }
  if (name === userInfoData.value.nickname) {
    isEditingNickname.value = false;
    return;
  }
  updateUserInfo({ nickname: name });
}

function cancelNickname() {
  isEditingNickname.value = false;
}

function updateUserInfo(data) {
  useredit({
    id: localStorage.getItem("userId"),
    ...data,
  })
    .then((res) => {
      if (res.code == 200) {
        ElMessage.success("修改成功");
        isEditingNickname.value = false;
        getUserInfo();
      } else {
        ElMessage.error(res.msg || "修改失败");
      }
    })
    .catch(() => {
      ElMessage.error("修改失败，请重试");
    });
}

function handleChangePassword() {
  passwordFormRef.value.validate((valid) => {
    if (!valid) return;
    if (passwordForm.oldPassword != userInfoData.value.password)
      return ElMessage.error("原密码输入不正确");
    submitting.value = true;
    useredit({
      id: localStorage.getItem("userId"),
      password: passwordForm.newPassword,
    })
      .then((res) => {
        if (res.code == 200) {
          ElMessage.success("密码修改成功");
          resetForm();
          getUserInfo();
        } else {
          ElMessage.error(res.msg || "密码修改失败");
        }
      })
      .catch(() => {
        ElMessage.error("密码修改失败，请重试");
      })
      .finally(() => {
        submitting.value = false;
      });
  });
}

onMounted(() => {
  EventBus.$on(EVENTS.SHOW_PROFILE_DIALOG, showDialog);
});

onBeforeUnmount(() => {
  EventBus.$off(EVENTS.SHOW_PROFILE_DIALOG, showDialog);
});
</script>

<style lang="scss" scoped>
.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 28px;
  border-bottom: 1px solid #2d2d2d;

  .dialog-title {
    margin: 0;
    font-size: 20px;
    font-weight: 500;
    color: #fff;
  }

  .close-icon {
    font-size: 24px;
    color: #888;
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: #fff;
    }
  }
}

.dialog-body {
  padding: 32px 28px;
}

.user-info-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 32px;

  .avatar-wrapper {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    overflow: hidden;
    margin-bottom: 20px;
    position: relative;
    cursor: pointer;

    .avatar-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .avatar-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.25s;

      .el-icon {
        font-size: 22px;
        color: #fff;
      }
    }

    &:hover .avatar-overlay {
      opacity: 1;
    }
  }

  .nickname-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;

    .nickname-text {
      font-size: 16px;
      color: #fff;
      max-width: 200px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .nickname-input {
      width: 180px;

      :deep(.el-input__wrapper) {
        background-color: #2d2d2d;
        box-shadow: 0 0 0 1px #3d3d3d inset !important;
      }

      :deep(.el-input__inner) {
        color: #fff;
        text-align: center;
        height: 36px;
        line-height: 36px;
      }

      :deep(.el-input__wrapper.is-focus) {
        box-shadow: 0 0 0 1px #8bff8b inset !important;
      }
    }

    .action-icon {
      font-size: 20px;
      width: 28px;
      height: 28px;
      cursor: pointer;
      transition: color 0.2s, transform 0.2s;

      svg {
        width: 22px;
        height: 22px;
      }

      &:hover {
        transform: scale(1.1);
      }
    }

    .edit-icon {
      color: #888;

      &:hover {
        color: #8bff8b;
      }
    }

    .confirm-icon {
      color: #8bff8b;

      &:hover {
        color: #a5ffa5;
      }
    }

    .cancel-icon {
      color: #888;

      &:hover {
        color: #ff6b6b;
      }
    }
  }
}

.password-section {
  .section-title {
    font-size: 16px;
    font-weight: 500;
    color: #fff;
    margin-bottom: 20px;
    padding-bottom: 12px;
    border-bottom: 1px solid #2d2d2d;
  }

  :deep(.el-form-item) {
    margin-bottom: 20px;
  }

  :deep(.el-input__wrapper) {
    background-color: #2d2d2d;
    box-shadow: 0 0 0 1px #3d3d3d inset !important;
  }

  :deep(.el-input__inner) {
    color: #fff;
    height: 42px;

    &::placeholder {
      color: #666;
    }
  }

  :deep(.el-input__wrapper.is-focus) {
    box-shadow: 0 0 0 1px #8bff8b inset !important;
  }

  :deep(.el-input__prefix),
  :deep(.el-input__suffix) {
    color: #888;
  }

  .submit-btn {
    width: 100%;
    height: 42px;
    background: linear-gradient(135deg, #8bff8b 0%, #6ee06e 100%);
    border: none;
    color: #1a1a1a;
    font-size: 15px;
    font-weight: 500;
    border-radius: 6px;

    &:hover {
      opacity: 0.9;
    }
  }
}
</style>
